import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { cariTypeLabel } from './cari-type-display';

export interface MoveCariTarget {
  id: string;
  companyName: string;
}

interface MoveCariTypeDialogProps {
  /** The supplier-side cari to move; null keeps the dialog closed. */
  cari: MoveCariTarget | null;
  /** The type the cari currently belongs to (excluded from targets). */
  currentTypeId: string;
  onOpenChange: (open: boolean) => void;
  /** Fired after a successful move so the caller can invalidate queries. */
  onMoved: (targetTypeCode?: string) => void;
}

// Move one cari to a different supplier-side type. 'M' never appears as a
// target — customers are a separate record kind (server enforces the same).
// Moves over posted history come back as CONFIRM_REQUIRED and are retried
// with confirmed=true after an explicit acknowledgement.
export function MoveCariTypeDialog({
  cari,
  currentTypeId,
  onOpenChange,
  onMoved,
}: MoveCariTypeDialogProps) {
  const { t, i18n } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const [targetTypeId, setTargetTypeId] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    setTargetTypeId('');
    setConfirmOpen(false);
  }, [cari?.id]);

  const { data: types } = trpc.tradingCariType.list.useQuery(undefined, { enabled: !!cari });

  const targetOptions: ComboboxOption[] = (types ?? [])
    .filter((tp) => tp.code !== 'M' && tp.id !== currentTypeId)
    .map((tp) => ({
      value: tp.id,
      label: `${tp.code} · ${cariTypeLabel(tp, i18n.language)}`,
      keywords: [tp.code, tp.name],
    }));

  const moveMutation = trpc.tradingCariType.moveCari.useMutation({
    onSuccess: (result) => {
      showSuccessToast(
        result.accountCleared ? t('cariTypes.cariMovedAccountCleared') : t('cariTypes.cariMoved')
      );
      setConfirmOpen(false);
      onOpenChange(false);
      onMoved(types?.find((tp) => tp.id === targetTypeId)?.code);
    },
    onError: (error) => {
      if (error.message.startsWith('CONFIRM_REQUIRED')) {
        setConfirmOpen(true);
      } else {
        showErrorToast(error.message);
      }
    },
  });

  const move = (confirmed: boolean) => {
    if (!cari || !targetTypeId) return;
    moveMutation.mutate({
      source: 'supplier',
      cariId: cari.id,
      targetTypeId,
      confirmed,
    });
  };

  return (
    <>
      <Dialog open={!!cari} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('cariTypes.moveCari')}</DialogTitle>
            <DialogDescription>
              {t('cariTypes.moveCariDesc', { name: cari?.companyName ?? '' })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            <Combobox
              options={targetOptions}
              value={targetTypeId}
              onValueChange={setTargetTypeId}
              placeholder={t('cariTypes.selectTargetType')}
              searchPlaceholder={t('cariTypes.searchAccount')}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                {tCommon('cancel')}
              </Button>
              <Button
                disabled={!targetTypeId || moveMutation.isPending}
                onClick={() => move(false)}
              >
                {moveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('cariTypes.moveAction')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Posted history / incompatible account assignment acknowledgement */}
      <ConfirmationDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('cariTypes.confirmMoveTitle')}
        description={t('cariTypes.confirmMove')}
        confirmLabel={t('cariTypes.moveAction')}
        onConfirm={() => move(true)}
        isLoading={moveMutation.isPending}
      />
    </>
  );
}
